import { errorIsStatus, useClientPortalAPI } from "@certn/api";
import {
  Avatar,
  BasicButton,
  ColumnDefinition,
  Container,
  DataTable,
  NeutralTag,
  Pagination,
  SectionMessage,
  SuccessTag,
  Text,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { formatDate, parseName } from "@certn/utils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ActionsDropdown } from "./ActionsDropdown";
import { StatusFilter } from "./components/StatusFilter";
import { useEmploymentVerificationTemplatesInPackages } from "./hooks";
import { FilterBar } from "../../components/filters/FilterBar";
import { PageTitle } from "../../components/page-title/PageTitle";
import { useQueryParams } from "../../hooks/useQueryParams";
import { Schemas } from "../../types";
import { queryParamsToApiParams } from "../utils";

function StatusTag({ isActive }: { isActive: boolean }) {
  const { t } = useTranslation();

  if (isActive) {
    return (
      <SuccessTag
        testID="employment-verification-template-active-tag"
        type="subtle"
      >
        {t("Active")}
      </SuccessTag>
    );
  }

  return (
    <NeutralTag testID="employment-verification-deactivated-tag" type="subtle">
      {t("Deactivated")}
    </NeutralTag>
  );
}

export function EmploymentVerificationTemplateListView() {
  const { t } = useTranslation();
  const { params, setParam } = useQueryParams();
  const navigate = useNavigate();
  const { data, isLoading, error, mutate } = useClientPortalAPI(
    `/api/internal/client-portal/check-arguments-templates/employment-verification/`,
    queryParamsToApiParams(params)
  );

  const templateIdsBelongingToActivePackages =
    useEmploymentVerificationTemplatesInPackages();

  const activeTemplates = data?.results.filter(
    (template) => template.is_active === true
  );

  const columns: ColumnDefinition<
    Schemas["EmploymentVerificationTemplateList"]
  >[] = [
    {
      key: "name",
      name: t("Name"),
      isRowHeader: true,
      allowsSorting: true,
      testID: "name-column-header",
      render({ name }: { name: string }) {
        return (
          <Text
            style="form/body-secondary"
            testID="employment-verification-template-name"
          >
            {name}
          </Text>
        );
      },
    },
    {
      key: "is_active",
      name: t("Status"),
      isRowHeader: true,
      allowsSorting: true,
      testID: "status-column-header",
      render({ is_active }: { is_active: boolean }) {
        return <StatusTag isActive={is_active} />;
      },
    },
    {
      key: "created",
      name: t("Created date"),
      isRowHeader: true,
      allowsSorting: true,
      testID: "created-date-column-header",
      render({ created }: { created: string }) {
        return (
          <Text style="form/body-secondary" testID="created-date">
            {formatDate(created, "MMM dd, yyyy")}
          </Text>
        );
      },
    },
    {
      key: "created_by",
      name: t("Created by"),
      isRowHeader: true,
      testID: "created-by-column-header",
      render(item: Schemas["EmploymentVerificationTemplateList"]) {
        if (item.created_by) {
          return (
            <Avatar
              size="small"
              name={parseName(item.created_by.auth0_full_name || undefined)}
            />
          );
        }
      },
    },
    {
      key: "actions",
      isAction: true,
      width: 48,
      render(template: Schemas["EmploymentVerificationTemplateList"]) {
        return (
          <ActionsDropdown
            templateIdsBelongingToActivePackages={
              templateIdsBelongingToActivePackages
            }
            numActiveTemplates={activeTemplates?.length || 0}
            mutate={mutate}
            templateId={template.id}
            name={template.name}
            isActive={template.is_active}
          />
        );
      },
    },
  ];

  return (
    <Container layout="full-width">
      <PageTitle
        actions={
          <BasicButton
            testID="new-template-button"
            onPress={() =>
              navigate("/settings/employment-verification-templates/create")
            }
          >
            {t("New template")}
          </BasicButton>
        }
      >
        {t("Employment verification")}
      </PageTitle>
      <FilterBar hasSearch testID="employment-verification">
        <StatusFilter />
      </FilterBar>
      {error && !errorIsStatus(error, 404) && (
        <SectionMessage
          type="danger"
          icon="error"
          sectionTitle={t("Unable to retrieve templates")}
        >
          {String(error.message)}
        </SectionMessage>
      )}
      <DataTable
        isLoading={isLoading}
        onSortChange={(sortDescriptor) =>
          setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
        }
        onRowAction={(id) => {
          navigate(`/settings/employment-verification-templates/${id}`);
        }}
        sortDescriptor={orderingParamToSortDescriptor(params.ordering)}
        columns={columns}
        rows={data?.results ? data.results : []}
        aria-label={t("Employment Verification Templates Table")}
        testId="employment-verification-template-list-view-data-table"
      />
      <Pagination
        setPage={(page) => setParam("page", page.toString())}
        setPageCount={(pageCount) =>
          setParam("page_size", pageCount.toString())
        }
        total={data?.count ?? 0}
        page={params.page ? Number(params.page) : 1}
        pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
      />
    </Container>
  );
}
